import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiMapPin,
    FiMaximize2,
    FiArrowLeft,
    FiAward,
    FiCalendar,
    FiShield,
    FiCheckCircle,
    FiShoppingBag,
    FiUsers,
    FiPlay,
    FiImage,
    FiVideo,
    FiAlertCircle,
    FiRefreshCw,
} from 'react-icons/fi';
import { useLightbox } from '../context/LightboxContext';
import { BUSINESS_MAP_URL, BUSINESS_ADDRESS_SINGLE, BUSINESS_HOURS, BUSINESS_PHONE_MAIN } from '../config/businessInfo';
import onGroundMediaService from '../services/onGroundMediaService';
import teamService from '../services/teamService';
import useRealtimeSync from '../hooks/useRealtimeSync';
import VideoModal from '../components/home/VideoModal';
import './AboutPage.css';

// Authentic Founder & Managing Director photo fallback
const FOUNDER_PHOTO_URL = '/assets/media/images/team/owner.jpeg';

const AnimatedCounter = ({ target, suffix = '', prefix = '', start = 0 }) => {
    const [count, setCount] = React.useState(start);

    React.useEffect(() => {
        let startTime = null;
        const duration = 1600;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeProgress = 1 - (1 - progress) * (1 - progress);
            const current = Math.round(start + (target - start) * easeProgress);
            setCount(current);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        const frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [target, start]);

    return (
        <span className="pill-num">
            {prefix}{count}{suffix}
        </span>
    );
};

const AboutPage = () => {
    const navigate = useNavigate();
    const { openLightbox } = useLightbox();

    const [showroomMedia, setShowroomMedia] = React.useState([]);
    const [showroomLoading, setShowroomLoading] = React.useState(true);
    const [showroomError, setShowroomError] = React.useState('');
    const [activeShowroomVideo, setActiveShowroomVideo] = React.useState(null);
    const [failedShowroomMediaIds, setFailedShowroomMediaIds] = React.useState({});
    const [failedShowroomThumbIds, setFailedShowroomThumbIds] = React.useState({});

    const [isTeamSectionEnabled, setIsTeamSectionEnabled] = React.useState(false);
    const [teamSectionChecked, setTeamSectionChecked] = React.useState(false);
    const [teamMembers, setTeamMembers] = React.useState([]);
    const [teamLoading, setTeamLoading] = React.useState(true);
    const [teamError, setTeamError] = React.useState(null);

    const isMountedRef = React.useRef(true);
    const latestShowroomFetchIdRef = React.useRef(0);
    const latestFetchIdRef = React.useRef(0);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchShowroomMedia = React.useCallback(async ({ showLoading = true } = {}) => {
        const fetchId = ++latestShowroomFetchIdRef.current;

        try {
            if (showLoading) {
                setShowroomLoading(true);
            }
            setShowroomError('');

            const items = await onGroundMediaService.getActiveMedia({
                active_only: true,
            });

            if (!isMountedRef.current || fetchId !== latestShowroomFetchIdRef.current) {
                return;
            }

            const normalizedList = Array.isArray(items) ? items : [];
            setShowroomMedia(normalizedList);
            setFailedShowroomMediaIds({});
            setFailedShowroomThumbIds({});

            setActiveShowroomVideo((prevVideo) => {
                if (!prevVideo) return null;
                const updatedItem = normalizedList.find(
                    (entry) =>
                        entry.mediaType === 'video' &&
                        (entry.id === prevVideo.id || entry._id === prevVideo._id)
                );
                return updatedItem ? updatedItem.videoModalData : null;
            });
        } catch (err) {
            if (!isMountedRef.current || fetchId !== latestShowroomFetchIdRef.current) {
                return;
            }

            if (showLoading) {
                setShowroomMedia([]);
                setShowroomError(
                    err?.message ||
                        'Unable to load showroom media right now. Please try again.'
                );
            }
        } finally {
            if (
                isMountedRef.current &&
                fetchId === latestShowroomFetchIdRef.current &&
                showLoading
            ) {
                setShowroomLoading(false);
            }
        }
    }, []);

    const fetchTeamSection = React.useCallback(
        async ({ showLoading = true, forceConfigProbe = false } = {}) => {
            const fetchId = ++latestFetchIdRef.current;

            try {
                if (showLoading) {
                    setTeamLoading(true);
                }
                setTeamError(null);

                const result = await teamService.getTeamSectionData({ forceConfigProbe });
                if (!isMountedRef.current || fetchId !== latestFetchIdRef.current) return;

                setIsTeamSectionEnabled(Boolean(result.isEnabled));
                setTeamMembers(Array.isArray(result.members) ? result.members : []);

                if (result.isEnabled && result.hasError) {
                    setTeamError('Failed to load team members.');
                } else {
                    setTeamError(null);
                }
            } catch (err) {
                if (!isMountedRef.current || fetchId !== latestFetchIdRef.current) return;
                setIsTeamSectionEnabled(false);
                setTeamError(null);
            } finally {
                if (isMountedRef.current && fetchId === latestFetchIdRef.current) {
                    setTeamLoading(false);
                    setTeamSectionChecked(true);
                }
            }
        },
        []
    );

    React.useEffect(() => {
        fetchShowroomMedia({ showLoading: true });
    }, [fetchShowroomMedia]);

    React.useEffect(() => {
        fetchTeamSection({ showLoading: true });
    }, [fetchTeamSection]);

    // Re-sync silently when user returns to the browser tab so media and visibility stay fresh without refresh
    React.useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchShowroomMedia({ showLoading: false });
                fetchTeamSection({ showLoading: false, forceConfigProbe: true });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchShowroomMedia, fetchTeamSection]);

    // Real-time SSE synchronization for Flagship Showroom / On-Ground Media
    const handleRealtimeShowroomMediaUpdate = React.useCallback(() => {
        fetchShowroomMedia({ showLoading: false });
    }, [fetchShowroomMedia]);

    useRealtimeSync('on_ground_media', handleRealtimeShowroomMediaUpdate);
    useRealtimeSync('on-ground-media', handleRealtimeShowroomMediaUpdate);
    useRealtimeSync('ongroundmedia', handleRealtimeShowroomMediaUpdate);
    useRealtimeSync('shop_gallery', handleRealtimeShowroomMediaUpdate);
    useRealtimeSync('shop-gallery', handleRealtimeShowroomMediaUpdate);
    useRealtimeSync('gallery', handleRealtimeShowroomMediaUpdate);

    // Real-time SSE synchronization for section visibility and team member updates
    const handleRealtimeTeamUpdate = React.useCallback(
        (payload) => {
            const directVisibility = teamService.extractVisibilityFromRealtimePayload(payload);
            if (directVisibility !== null) {
                setIsTeamSectionEnabled(directVisibility);
                setTeamSectionChecked(true);
            }
            fetchTeamSection({ showLoading: false, forceConfigProbe: true });
        },
        [fetchTeamSection]
    );

    useRealtimeSync('team', handleRealtimeTeamUpdate);
    useRealtimeSync('team_members', handleRealtimeTeamUpdate);
    useRealtimeSync('team_section', handleRealtimeTeamUpdate);
    useRealtimeSync('sections', handleRealtimeTeamUpdate);
    useRealtimeSync('settings', handleRealtimeTeamUpdate);

    const handleShowroomCardOpen = React.useCallback(
        (item) => {
            if (!item) return;

            if (item.mediaType === 'video' && item.videoModalData) {
                setActiveShowroomVideo(item.videoModalData);
                return;
            }

            if (item.imageUrl && !failedShowroomMediaIds[item.id]) {
                openLightbox(item.imageUrl, item.title, item.desc);
            }
        },
        [failedShowroomMediaIds, openLightbox]
    );

    const markShowroomMediaFailed = React.useCallback((id) => {
        setFailedShowroomMediaIds((prev) => ({ ...prev, [id]: true }));
    }, []);

    const markShowroomThumbFailed = React.useCallback((id) => {
        setFailedShowroomThumbIds((prev) => ({ ...prev, [id]: true }));
    }, []);

    return (
        <div className="about-page">
            {/* Top Hero Section */}
            <div className="about-hero">
                <div className="container">
                    <div style={{ marginBottom: '0.85rem' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>
                    <span className="section-eyebrow about-hero-eyebrow">ESTABLISHED 1980 IN LAHORE</span>
                    <h1>About Rahber Corporation</h1>
                    <p>
                        The story, vision, and continuing leadership behind 45+ years of trust in household electronics across Lahore and Pakistan.
                    </p>
                </div>
            </div>

            <div className="container">
                <section className="about-content">
                    {/* Unified Master Legacy Section: Mr. Naeem Shafi (Founder) */}
                    <div className="about-section about-section--legacy">
                        <div className="about-legacy-layout">
                            {/* Visual Wrap: Executive Founder Portrait */}
                            <div className="about-legacy-visual-wrap">
                                <div
                                    className="about-legacy-circle-frame"
                                    onClick={() => openLightbox(FOUNDER_PHOTO_URL, 'Mr. Naeem Shafi', 'Managing Director & Founder: Established Rahber Corporation in 1980 in Lahore')}
                                    title="Click to view full portrait"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            openLightbox(FOUNDER_PHOTO_URL, 'Mr. Naeem Shafi', 'Managing Director & Founder: Established Rahber Corporation in 1980 in Lahore');
                                        }
                                    }}
                                >
                                    <div className="legacy-glow-ring legacy-glow-ring--blue" aria-hidden="true" />
                                    <div className="legacy-glow-ring legacy-glow-ring--orange" aria-hidden="true" />
                                    <img
                                        src={FOUNDER_PHOTO_URL}
                                        alt="Mr. Naeem Shafi, Managing Director and Founder of Rahber Corporation"
                                        className="about-legacy-circle-img"
                                        loading="lazy"
                                    />
                                    <div className="legacy-zoom-pill">
                                        <FiMaximize2 aria-hidden="true" />
                                        <span>View Photo</span>
                                    </div>
                                </div>

                                <div className="about-legacy-photo-badge">
                                    <strong>Mr. Naeem Shafi</strong>
                                    <span>Managing Director &amp; Founder</span>
                                </div>
                            </div>

                            {/* Info Column: Fully Centered & Beautifully Formatted Story */}
                            <div className="about-legacy-info">
                                {/* Centered Premium Header */}
                                <div className="about-story-header-center">
                                    <span className="about-story-tag-pill">
                                        <FiAward className="story-pill-icon" aria-hidden="true" />
                                        <span>45+ YEARS OF HERITAGE &amp; TRUST</span>
                                    </span>
                                    <h2 className="about-legacy-title">The Rahber Corporation Story</h2>
                                    <p className="about-legacy-subtitle">
                                        Founded in 1980 by Mr. Naeem Shafi in Lahore, Pakistan
                                    </p>
                                </div>

                                {/* Structured Story Milestone Cards (Centered & Mobile-Optimized) */}
                                <div className="about-story-cards-container">
                                    {/* Milestone 1: 1980 Visionary Foundation (Mr. Naeem Shafi) */}
                                    <div className="about-story-card about-story-card--founder">
                                        <div className="about-story-card-top">
                                            <span className="story-card-icon-disc story-card-icon-disc--orange">
                                                <FiCalendar aria-hidden="true" />
                                            </span>
                                            <div className="story-card-badge-group">
                                                <span className="story-card-badge">FOUNDING VISION: 1980</span>
                                                <strong className="story-card-heading">Mr. Naeem Shafi: The Heritage Begins</strong>
                                            </div>
                                        </div>

                                        <div className="about-story-card-prose">
                                            <p className="about-story-card-text">
                                                Established in <strong>1980</strong> in Lahore by <strong>Mr. Naeem Shafi</strong> as the visionary founder, Rahber Corporation started with quality ceiling fans and household electronics.
                                            </p>
                                            <p className="about-story-card-text">
                                                Built on the unshakable pillars of honest dealing, fair rates, and transparent customer care, his leadership established a generational legacy of trust that serves thousands of families across Lahore.
                                            </p>
                                            <p className="about-story-card-text">
                                                Today, Rahber Corporation represents leading official partner brands including <strong>Dawlance, PEL, Orient, Multynet, Konvo, TCL, Gree, Eco Star, United, Super Asia, Waves, Samsung</strong>, and more, delivering authentic box-packed appliances with official warranties and easy installment plans.
                                            </p>
                                        </div>

                                        <div className="about-story-chips-row">
                                            <span className="story-chip story-chip--orange">
                                                <FiCheckCircle aria-hidden="true" /> 1980 Lahore Roots
                                            </span>
                                            <span className="story-chip story-chip--orange">
                                                <FiCheckCircle aria-hidden="true" /> 100% Honest Dealing
                                            </span>
                                            <span className="story-chip story-chip--orange">
                                                <FiCheckCircle aria-hidden="true" /> Generational Trust
                                            </span>
                                            <span className="story-chip story-chip--orange">
                                                <FiCheckCircle aria-hidden="true" /> Official Brand Warranties
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Styled Centered Quote Box */}
                                <div className="about-quote-box">
                                    <div className="about-quote-decoration" aria-hidden="true">&ldquo;</div>
                                    <p className="about-quote-content">
                                        We don&apos;t just sell appliances; we deliver genuine warranties, transparent pricing, and lasting peace of mind to every household in Lahore.
                                    </p>
                                    <cite className="about-quote-author">
                                        Mr. Naeem Shafi, Founder &amp; Managing Director
                                    </cite>
                                    <span className="about-quote-role">
                                        Rahber Corporation • Established 1980
                                    </span>
                                </div>

                                {/* 3 Balanced Stat Highlights */}
                                <div className="about-stats-row-pills">
                                    <div className="about-stat-pill">
                                        <AnimatedCounter target={1980} start={1950} />
                                        <span className="pill-txt">Established in Lahore</span>
                                    </div>
                                    <div className="about-stat-pill">
                                        <AnimatedCounter target={45} start={0} suffix="+ Years" />
                                        <span className="pill-txt">Unbroken Customer Trust</span>
                                    </div>
                                    <div className="about-stat-pill">
                                        <AnimatedCounter target={100} start={0} suffix="%" />
                                        <span className="pill-txt">Box-Packed Guaranteed</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Showroom Visual Showcase (Dynamic via On-Ground Media API) */}
                    <div className="about-section about-section--gallery">
                        <div className="about-section-header-center">
                            <span className="about-story-tag-pill">
                                <FiShoppingBag className="story-pill-icon" aria-hidden="true" />
                                <span>FLAGSHIP SHOWROOM</span>
                            </span>
                            <h2 className="about-section-title-center">Our Showroom Experience</h2>
                            <p className="about-gallery-intro">
                                Experience genuine home appliances in person at our flagship showroom in Lahore. Tap any photo to view full details.
                            </p>
                        </div>

                        {showroomLoading && showroomMedia.length === 0 ? (
                            <div
                                className="about-gallery-grid"
                                aria-label="Loading showroom media"
                            >
                                {[0, 1, 2, 3, 4, 5].map((idx) => (
                                    <div
                                        key={`about-gallery-skeleton-${idx}`}
                                        className="about-gallery-item about-gallery-item--skeleton"
                                        aria-hidden="true"
                                    >
                                        <div className="about-gallery-media-wrap about-gallery-skeleton-shimmer" />
                                        <div className="about-gallery-meta">
                                            <span className="about-gallery-skeleton-line about-gallery-skeleton-shimmer" />
                                            <span className="about-gallery-skeleton-dot about-gallery-skeleton-shimmer" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : showroomError && showroomMedia.length === 0 ? (
                            <div
                                className="about-gallery-state-box about-gallery-state-box--error"
                                role="alert"
                            >
                                <FiAlertCircle
                                    className="about-gallery-state-icon"
                                    aria-hidden="true"
                                />
                                <p className="about-gallery-state-text">{showroomError}</p>
                                <button
                                    type="button"
                                    className="about-gallery-retry-btn"
                                    onClick={() => fetchShowroomMedia({ showLoading: true })}
                                >
                                    <FiRefreshCw aria-hidden="true" />
                                    <span>Retry</span>
                                </button>
                            </div>
                        ) : showroomMedia.length === 0 ? (
                            <div className="about-gallery-state-box">
                                <FiImage
                                    className="about-gallery-state-icon"
                                    aria-hidden="true"
                                />
                                <p className="about-gallery-state-text">
                                    No showroom media has been published yet.
                                </p>
                            </div>
                        ) : (
                            <div className="about-gallery-grid">
                                {showroomMedia.map((item) => {
                                    const isVideo = item.mediaType === 'video';
                                    const isMediaBroken = Boolean(
                                        failedShowroomMediaIds[item.id]
                                    );
                                    const hasVideoThumb =
                                        isVideo &&
                                        Boolean(item.thumbnailUrl) &&
                                        !failedShowroomThumbIds[item.id];
                                    const canPreviewDirectVideo =
                                        isVideo &&
                                        !hasVideoThumb &&
                                        item.isDirectVideo &&
                                        Boolean(item.videoSrc) &&
                                        !isMediaBroken;
                                    const previewVideoUrl =
                                        canPreviewDirectVideo &&
                                        !item.videoSrc.includes('#')
                                            ? `${item.videoSrc}#t=0.1`
                                            : item.videoSrc;

                                    return (
                                        <div
                                            key={item.id}
                                            className={`about-gallery-item ${
                                                isVideo ? 'about-gallery-item--video' : ''
                                            }`}
                                            onClick={() => handleShowroomCardOpen(item)}
                                            title={
                                                isVideo
                                                    ? `Click to play video: ${item.title}`
                                                    : `Click to view full photo: ${item.title}`
                                            }
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleShowroomCardOpen(item);
                                                }
                                            }}
                                        >
                                            <div className="about-gallery-media-wrap">
                                                {isVideo ? (
                                                    hasVideoThumb ? (
                                                        <img
                                                            src={item.thumbnailUrl}
                                                            alt={item.altText}
                                                            className="about-gallery-img"
                                                            loading="lazy"
                                                            onError={() =>
                                                                markShowroomThumbFailed(item.id)
                                                            }
                                                        />
                                                    ) : canPreviewDirectVideo ? (
                                                        <video
                                                            src={previewVideoUrl}
                                                            muted
                                                            playsInline
                                                            preload="metadata"
                                                            disablePictureInPicture
                                                            aria-hidden="true"
                                                            tabIndex={-1}
                                                            className="about-gallery-img"
                                                            onError={() =>
                                                                markShowroomMediaFailed(item.id)
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="about-gallery-media-fallback">
                                                            <FiVideo size={26} />
                                                            <span>{item.title}</span>
                                                        </div>
                                                    )
                                                ) : !isMediaBroken ? (
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={item.altText}
                                                        className="about-gallery-img"
                                                        loading="lazy"
                                                        onError={() =>
                                                            markShowroomMediaFailed(item.id)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="about-gallery-media-fallback">
                                                        <FiImage size={26} />
                                                        <span>{item.title}</span>
                                                    </div>
                                                )}

                                                {isVideo && (
                                                    <div
                                                        className="about-gallery-play-badge"
                                                        aria-hidden="true"
                                                    >
                                                        <FiPlay className="about-gallery-play-badge-icon" />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="about-gallery-meta">
                                                <span className="about-gallery-label">
                                                    {item.title}
                                                </span>
                                                <span
                                                    className="about-gallery-zoom-icon"
                                                    aria-hidden="true"
                                                >
                                                    {isVideo ? <FiPlay /> : <FiMaximize2 />}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Dedicated Showroom Team Section (Rendered only when enabled in Admin Dashboard) */}
                    {teamSectionChecked && isTeamSectionEnabled && (
                        <div className="about-section about-section--team">
                            <div className="about-section-header-center">
                                <span className="about-story-tag-pill">
                                    <FiUsers className="story-pill-icon" aria-hidden="true" />
                                    <span>OUR DEDICATED TEAM</span>
                                </span>
                                <h2 className="about-section-title-center">Meet the Rahber Showroom Team</h2>
                                <p className="about-gallery-intro">
                                    Behind 45+ years of trust are real, dedicated professionals. Meet the team who serve you daily at our Lahore showroom.
                                </p>
                            </div>

                            <div className="about-team-grid">
                                {teamLoading ? (
                                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem 0', color: 'var(--gray-500)' }}>
                                        <p>Loading dedicated team members...</p>
                                    </div>
                                ) : teamError ? (
                                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem 0', color: 'var(--primary-red)' }}>
                                        <p>{teamError}</p>
                                        <button
                                            type="button"
                                            onClick={() => fetchTeamSection({ showLoading: true, forceConfigProbe: true })}
                                            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'var(--primary-blue)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                ) : teamMembers.length === 0 ? (
                                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem 0', color: 'var(--gray-500)' }}>
                                        <p>No team members available at the moment.</p>
                                    </div>
                                ) : (
                                    teamMembers.map((member) => (
                                        <div
                                            key={member.id}
                                            className="about-team-card"
                                            onClick={() => openLightbox(member.image, member.name, [member.role, member.experience].filter(Boolean).join(' — '))}
                                            role="button"
                                            tabIndex={0}
                                            title={`Click to view full photo of ${member.name}`}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    openLightbox(member.image, member.name, [member.role, member.experience].filter(Boolean).join(' — '));
                                                }
                                            }}
                                        >
                                            <div className="about-team-photo-wrap">
                                                <img
                                                    src={member.image}
                                                    alt={`${member.name} - ${member.role} at Rahber Corporation`}
                                                    className="about-team-photo"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.src = '/assets/media/images/team/ceo.jpg';
                                                    }}
                                                />
                                                {member.badge && (
                                                    <span className="about-team-badge">{member.badge}</span>
                                                )}
                                                <div className="about-team-zoom-pill" aria-hidden="true">
                                                    <FiMaximize2 />
                                                    <span>Zoom</span>
                                                </div>
                                            </div>
                                            <div className="about-team-card-content">
                                                <h3 className="about-team-name">{member.name}</h3>
                                                {member.role && <div className="about-team-role">{member.role}</div>}
                                                {member.experience && (
                                                    <div className="about-team-exp">
                                                        <FiCheckCircle className="team-exp-icon" aria-hidden="true" />
                                                        <span>{member.experience}</span>
                                                    </div>
                                                )}
                                                {member.bio && <p className="about-team-bio">{member.bio}</p>}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* What We Stand For */}
                    <div className="about-section about-section--pillars">
                        <div className="about-section-header-center">
                            <span className="about-story-tag-pill">
                                <FiShield className="story-pill-icon" aria-hidden="true" />
                                <span>CORE VALUES &amp; STANDARDS</span>
                            </span>
                            <h2 className="about-section-title-center">What We Stand For</h2>
                            <p className="about-gallery-intro">
                                Six foundational principles that guide every interaction, customer sale, and installment plan.
                            </p>
                        </div>

                        <div className="features-list">
                            <div className="feature-item">
                                <div className="feature-number">01</div>
                                <div className="feature-content">
                                    <h3>Trust and transparency</h3>
                                    <p>Honest dealings in every sale and installment plan. Zero hidden markups or surprises.</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-number">02</div>
                                <div className="feature-content">
                                    <h3>Affordable living</h3>
                                    <p>Essential cooling and home appliances within comfortable reach for every household budget.</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-number">03</div>
                                <div className="feature-content">
                                    <h3>Supporting local businesses</h3>
                                    <p>Reliable wholesale supply and dealership support for retailers across Pakistan.</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-number">04</div>
                                <div className="feature-content">
                                    <h3>Easy access</h3>
                                    <p>Flexible ways to purchase: Instant cash discounts on MRP, or easy 4 to 12 months installment plans (4 month 0% markup) in Lahore.</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-number">05</div>
                                <div className="feature-content">
                                    <h3>Long-term relationships</h3>
                                    <p>Serving families and business partners across generations with dedicated care.</p>
                                </div>
                            </div>
                            <div className="feature-item">
                                <div className="feature-number">06</div>
                                <div className="feature-content">
                                    <h3>Dedicated customer support</h3>
                                    <p>Attentive showroom guidance, phone assistance, and fast official warranty processing.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Showroom Address and Directions */}
                    <div className="about-section about-section--showroom">
                        <div className="about-section-header-center">
                            <span className="about-story-tag-pill">
                                <FiMapPin className="story-pill-icon" aria-hidden="true" />
                                <span>LOCATION &amp; TIMINGS</span>
                            </span>
                            <h2 className="about-section-title-center">Visit Our Lahore Showroom</h2>
                            <p className="about-gallery-intro">
                                We welcome you to visit our flagship showroom in person to inspect box-packed products, consult our team, and apply for instant installments.
                            </p>
                        </div>

                        <div className="about-showroom-box">
                            <div className="about-showroom-info">
                                <FiMapPin className="showroom-pin-icon" aria-hidden="true" />
                                <div>
                                    <strong>{BUSINESS_ADDRESS_SINGLE}</strong>
                                    <span>{BUSINESS_HOURS.lines[0]} • Ph: {BUSINESS_PHONE_MAIN[0].display}</span>
                                </div>
                            </div>
                            <a
                                href={BUSINESS_MAP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="about-map-btn"
                            >
                                Open Directions on Google Maps →
                            </a>
                        </div>
                    </div>
                </section>
            </div>

            {/* Video Playback Modal for Showroom Video Items */}
            <VideoModal
                isOpen={Boolean(activeShowroomVideo)}
                video={activeShowroomVideo}
                onClose={() => setActiveShowroomVideo(null)}
            />
        </div>
    );
};

export default AboutPage;
